import BaseCashoutService from './base-cashout-service.js';
import BetsTypes from 'services/bets/types.js';

const { PUSH } = BetsTypes.CASHOUT_PROTOCOLS;
const voidMapping = logger => () => logger.error(`Wrong configuration. dataMapper is missing.`);

const NULL_DATA_MAPPER = logger => {
    return {
        mapRequest: voidMapping(logger),
        mapResponse: voidMapping(logger),
    };
};

/**
 * Cashout service config.
 *
 * @typedef {Mojito.Services.Bets.cashoutService.CashoutServiceConfig} PushCashoutServiceConfig
 * @property {Mojito.Services.Bets.types.ICashoutOffersMapper} dataMapper - Cashout offers data mapper implementation.
 * Will be called to map messages to/from API specific structures.
 *
 * @memberof Mojito.Services.Bets.pushCashoutService
 */

/**
 * Class offering the possibility to interact with the mojito bets cash out API using
 * {@link Mojito.Services.Bets.types.CASHOUT_PROTOCOLS.PUSH|PUSH} transport to retrieve cash out offers.
 *
 * @class PushCashoutService
 * @name pushCashoutService
 * @extends Mojito.Services.Bets.BaseCashoutService
 * @memberof Mojito.Services.Bets
 */
export class PushCashoutService extends BaseCashoutService {
    constructor() {
        super(PUSH);
        this.socket = undefined;
        this.reconnectAttempts = 0;
    }

    /**
     * Configure service.
     *
     * @param {Mojito.Services.Bets.pushCashoutService.PushCashoutServiceConfig} config - Service configuration object.
     *
     * @function Mojito.Services.Bets.pushCashoutService#configure
     */
    configure({ serviceUrl, dataMapper, maxReconnectAttempts }) {
        super.configure({ serviceUrl });
        this.dataMapper = dataMapper || NULL_DATA_MAPPER(this.logger);
        this.getSubscriptionOptions = this.getSubscriptionOptions.bind(this);
        this.maxReconnectAttempts = maxReconnectAttempts || 5;
    }

    createWebSocket(url) {
        return new WebSocket(url);
    }

    /**
     * Requests cashout offers for bets.
     *
     * @param {Array<Mojito.Services.Bets.types.BetInfo>} betInfos - List of bet info objects.
     * @param {Mojito.Services.Bets.types.cashoutOffersSuccess} success - Callback executed once cashout offers successfully fetched.
     * @param {Mojito.Services.Bets.types.cashoutValuesFail} fail - Callback executed once cashout offers failed to fetch.
     * @param {Mojito.Services.Bets.types.cashoutSocketClose} close - Callback executed once cashout socket closed.
     *
     * @returns {Promise} Promise on cashout offers request.
     *
     * @function Mojito.Services.Bets.pushCashoutService#requestCashoutOffers
     */
    requestCashoutOffers(betInfos, success, fail, close) {
        if (this.isRequested) {
            return Promise.resolve();
        }
        this.isRequested = true;
        return this.validateCashoutTransport()
            .then(this.getSubscriptionOptions)
            .then(this.subscribe(success, betInfos, close))
            .catch(error => {
                this.isRequested = false;
                fail(error);
            });
    }

    /**
     * Request a single cashout offer for one bet. Push transport does not support single-offer refresh; resolves with undefined.
     *
     * @returns {Promise<undefined>} Promise resolving with undefined.
     * @function Mojito.Services.Bets.pushCashoutService#requestCashoutOffer
     */
    requestCashoutOffer() {
        return Promise.resolve(undefined);
    }

    /**
     * Discard cash out values request made by {@link Mojito.Services.Bets.PushCashoutService#requestCashoutOffers|requestCashoutOffers}.
     *
     * @function Mojito.Services.Bets.pushCashoutService#discardCashoutOffers
     */
    discardCashoutOffers() {
        this.isRequested = false;
        this.reconnectAttempts = 0;
        this.invalidateCashoutTransport();
        if (!this.socket) {
            return;
        }
        this.socket.onopen = () => this.closeSocket();
        if (this.socket.readyState === WebSocket.OPEN) {
            this.closeSocket();
        }
    }

    closeSocket() {
        this.socket.onclose = undefined; // By adding this line we're not trying to reconnect in cases when we're doing the close on our own
        this.socket.close();
        this.socket = undefined;
    }

    getSubscriptionOptions() {
        if (!this.validateServiceUrl() || !this.isRequested) {
            return Promise.reject();
        }
        return new Promise((success, reject) => {
            this.requestFactory
                .post(`${this.apiUrl}/cashout-push-subscribe`)
                .withDescription('get subscription options')
                .withCredentials()
                .send()
                .then(success)
                .catch(reject);
        });
    }

    subscribe(onMessage, betInfos, onSocketClose) {
        const connectToWS = ({ token, url }) => {
            if (!this.isRequested) {
                return;
            }
            this.socket = this.createWebSocket(url);
            const offersRequest = this.dataMapper.mapRequest({ betInfos, token });

            this.socket.onopen = () => {
                this.reconnectAttempts = 0;
                offersRequest && this.socket.send(offersRequest);
            };
            this.socket.onmessage = msg => {
                const cashoutInfos = this.dataMapper.mapResponse(msg.data);
                cashoutInfos && onMessage({ cashoutInfos });
            };
            this.socket.onclose = event => {
                onSocketClose?.();

                this.reconnectAttempts++;

                if (this.reconnectAttempts > this.maxReconnectAttempts) {
                    this.logger.error(
                        `WebSocket reconnect failed after ${this.maxReconnectAttempts} attempts`
                    );
                    return;
                }

                if (!event.wasClean) {
                    this.logger.warn('WebSocket is closed abnormally. Trying to reconnect.', event);
                }

                const reconnect = event.wasClean
                    ? this.getSubscriptionOptions().catch(error => {
                          this.logger.error(
                              'Failed to get subscription options. Trying to reconnect using old options.',
                              error
                          );
                          return { token, url };
                      })
                    : Promise.resolve({ token, url });

                reconnect.then(connectToWS);
            };
            this.socket.onerror = event => this.logger.error('WebSocket error. ', event);
        };
        return connectToWS;
    }
}

export default new PushCashoutService();
